<script>
    import { onMount } from 'svelte';
    
    let searchTerm = '';
    let searchResults = [];
    let selectedStudent = null;
    let weeklySchedule = [];
    let messagePreview = '';
    let isLoading = false;
    let isSearching = false;
    let isSending = false;
    let successMessage = '';
    let errorMessage = '';
    let searchNextWeek = true; // Default to next week
    
    // Search for students
    async function searchStudents() {
        if (searchTerm.trim().length < 2) {
            searchResults = [];
            return;
        }
        
        isSearching = true;
        try {
            const response = await fetch('/studentplayground/api/admin/search-students/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`,
                },
                body: JSON.stringify({
                    search_term: searchTerm
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                searchResults = data.students;
            } else {
                console.error('Failed to search students');
                searchResults = [];
            }
        } catch (error) {
            console.error('Error searching students:', error);
            searchResults = [];
        } finally {
            isSearching = false;
        }
    }
    
    // Select a student and get their weekly schedule
    async function selectStudent(student) {
        selectedStudent = student;
        searchResults = [];
        searchTerm = `${student.first_name} ${student.last_name} (${student.username})`;
        
        await getWeeklySchedule(student.id);
    }
    
    // Get weekly schedule for selected student
    async function getWeeklySchedule(studentId) {
        isLoading = true;
        successMessage = '';
        errorMessage = '';
        
        try {
            const response = await fetch('/studentplayground/api/admin/get-student-weekly-schedule/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`,
                },
                body: JSON.stringify({
                    student_id: studentId,
                    search_next_week: searchNextWeek
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                weeklySchedule = data.classes;
                messagePreview = data.message_preview;
            } else {
                const errorData = await response.json();
                errorMessage = errorData.error || 'Failed to get weekly schedule';
                weeklySchedule = [];
                messagePreview = '';
            }
        } catch (error) {
            console.error('Error getting weekly schedule:', error);
            errorMessage = 'Error loading weekly schedule';
            weeklySchedule = [];
            messagePreview = '';
        } finally {
            isLoading = false;
        }
    }
    
    // Send weekly schedule SMS
    async function sendWeeklySchedule() {
        if (!selectedStudent) return;
        
        isSending = true;
        successMessage = '';
        errorMessage = '';
        
        try {
            const response = await fetch('/studentplayground/api/admin/send-student-weekly-schedule/', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('jwtToken')}`,
                },
                body: JSON.stringify({
                    student_id: selectedStudent.id,
                    search_next_week: searchNextWeek
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                successMessage = `SMS sent successfully to ${selectedStudent.first_name} ${selectedStudent.last_name}!`;
                if (data.message_sid) {
                    successMessage += ` (Message ID: ${data.message_sid})`;
                }
            } else {
                const errorData = await response.json();
                errorMessage = errorData.error || 'Failed to send SMS';
            }
        } catch (error) {
            console.error('Error sending SMS:', error);
            errorMessage = 'Error sending SMS message';
        } finally {
            isSending = false;
        }
    }
    
    // Clear selection
    function clearSelection() {
        selectedStudent = null;
        weeklySchedule = [];
        messagePreview = '';
        searchTerm = '';
        searchResults = [];
        successMessage = '';
        errorMessage = '';
    }
    
    // Reactive search
    $: if (searchTerm) {
        searchStudents();
    }
</script>

<div class="send-weekly-sms-container">
    <div class="header">
        <h2>Send Weekly Schedule SMS</h2>
        <p>Search for a student and send them their weekly class schedule via SMS</p>
    </div>
    
    <!-- Week Selection -->
    <div class="week-selection">
        <label class="checkbox-container">
            <input 
                type="checkbox" 
                bind:checked={searchNextWeek}
                on:change={() => {
                    if (selectedStudent) {
                        getWeeklySchedule(selectedStudent.id);
                    }
                }}
            />
            <span class="checkmark"></span>
            Search Next Week (unchecked = search remaining classes this week)
        </label>
    </div>
    
    <!-- Student Search -->
    <div class="search-section">
        <div class="search-input-container">
            <input
                type="text"
                bind:value={searchTerm}
                placeholder="Search students by name, username, or email..."
                class="search-input"
                disabled={selectedStudent}
            />
            {#if isSearching}
                <div class="search-spinner">🔍</div>
            {/if}
        </div>
        
        <!-- Search Results -->
        {#if searchResults.length > 0 && !selectedStudent}
            <div class="search-results">
                {#each searchResults as student}
                    <div 
                        class="search-result-item" 
                        tabindex="0"
                        role="button"
                        on:click={() => selectStudent(student)}
                        on:keydown={(e) => e.key === 'Enter' || e.key === ' ' ? selectStudent(student) : null}
                    >
                        <div class="student-info">
                            <div class="student-name">{student.first_name} {student.last_name}</div>
                            <div class="student-details">@{student.username} • {student.email}</div>
                            {#if student.phone_number}
                                <div class="student-phone">📱 {student.phone_number}</div>
                            {:else}
                                <div class="student-phone no-phone">❌ No phone number</div>
                            {/if}
                        </div>
                    </div>
                {/each}
            </div>
        {/if}
        
        {#if searchResults.length === 0 && searchTerm.trim().length >= 2 && !isSearching && !selectedStudent}
            <div class="no-results">No students found matching "{searchTerm}"</div>
        {/if}
    </div>
    
    <!-- Selected Student & Schedule Preview -->
    {#if selectedStudent}
        <div class="selected-student-section">
            <div class="selected-student-header">
                <div class="student-card">
                    <div class="student-name">{selectedStudent.first_name} {selectedStudent.last_name}</div>
                    <div class="student-details">@{selectedStudent.username} • {selectedStudent.email}</div>
                    {#if selectedStudent.phone_number}
                        <div class="student-phone">📱 {selectedStudent.phone_number}</div>
                    {:else}
                        <div class="student-phone no-phone">❌ No phone number - Cannot send SMS</div>
                    {/if}
                </div>
                <button class="clear-button" on:click={clearSelection}>Change Student</button>
            </div>
            
            {#if isLoading}
                <div class="loading">Loading weekly schedule...</div>
            {:else if weeklySchedule.length > 0}
                <div class="schedule-preview">
                    <h3>Classes for {searchNextWeek ? 'Next Week' : 'This Week (Remaining)'}</h3>
                    <div class="classes-list">
                        {#each weeklySchedule as classItem}
                            <div class="class-item">
                                <div class="class-day-time">{classItem.day} {classItem.time}</div>
                                <div class="class-subject">{classItem.subject}</div>
                                <div class="class-teacher">Teacher: {classItem.teacher}</div>
                            </div>
                        {/each}
                    </div>
                    
                    <div class="message-preview-section">
                        <h4>SMS Message Preview:</h4>
                        <div class="message-preview">{messagePreview}</div>
                    </div>
                    
                    <!-- Send Button -->
                    <div class="send-section">
                        {#if selectedStudent.phone_number}
                            <button 
                                class="send-button" 
                                on:click={sendWeeklySchedule}
                                disabled={isSending}
                            >
                                {#if isSending}
                                    Sending SMS...
                                {:else}
                                    📱 Send Weekly Schedule SMS
                                {/if}
                            </button>
                        {:else}
                            <div class="no-phone-warning">
                                ❌ Cannot send SMS - Student has no phone number on file
                            </div>
                        {/if}
                    </div>
                </div>
            {:else if !isLoading}
                <div class="no-classes">
                    <h3>No Upcoming Classes</h3>
                    <p>This student has no classes scheduled for the upcoming week.</p>
                </div>
            {/if}
        </div>
    {/if}
    
    <!-- Success/Error Messages -->
    {#if successMessage}
        <div class="success-message">✅ {successMessage}</div>
    {/if}
    
    {#if errorMessage}
        <div class="error-message">❌ {errorMessage}</div>
    {/if}
</div>

<style>
    .send-weekly-sms-container {
        max-width: 800px;
        margin: 0 auto;
        padding: 20px;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    
    .header {
        text-align: center;
        margin-bottom: 30px;
    }
    
    .header h2 {
        color: #333;
        margin-bottom: 10px;
    }
    
    .header p {
        color: #666;
        font-size: 14px;
    }
    
    .week-selection {
        margin-bottom: 25px;
        padding: 15px;
        background: #f8f9fa;
        border-radius: 8px;
        border: 1px solid #dee2e6;
    }
    
    .checkbox-container {
        display: flex;
        align-items: center;
        cursor: pointer;
        font-size: 16px;
        color: #333;
        user-select: none;
    }
    
    .checkbox-container input[type="checkbox"] {
        margin-right: 10px;
        transform: scale(1.2);
        cursor: pointer;
    }
    
    .search-section {
        margin-bottom: 30px;
    }
    
    .search-input-container {
        position: relative;
        margin-bottom: 15px;
    }
    
    .search-input {
        width: 100%;
        padding: 15px;
        border: 2px solid #ddd;
        border-radius: 8px;
        font-size: 16px;
        box-sizing: border-box;
    }
    
    .search-input:focus {
        outline: none;
        border-color: #007bff;
    }
    
    .search-spinner {
        position: absolute;
        right: 15px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 18px;
    }
    
    .search-results {
        border: 1px solid #ddd;
        border-radius: 8px;
        max-height: 300px;
        overflow-y: auto;
        background: white;
        box-shadow: 0 2px 10px rgba(0,0,0,0.1);
    }
    
    .search-result-item {
        padding: 15px;
        border-bottom: 1px solid #eee;
        cursor: pointer;
        transition: background-color 0.2s;
        outline: none;
    }
    
    .search-result-item:hover,
    .search-result-item:focus {
        background-color: #f5f5f5;
    }
    
    .search-result-item:focus {
        box-shadow: 0 0 0 2px #007bff;
    }
    
    .search-result-item:last-child {
        border-bottom: none;
    }
    
    .student-info {
        display: flex;
        flex-direction: column;
        gap: 5px;
    }
    
    .student-name {
        font-weight: bold;
        color: #333;
    }
    
    .student-details {
        color: #666;
        font-size: 14px;
    }
    
    .student-phone {
        color: #007bff;
        font-size: 14px;
    }
    
    .student-phone.no-phone {
        color: #dc3545;
    }
    
    .no-results {
        padding: 20px;
        text-align: center;
        color: #666;
        font-style: italic;
    }
    
    .selected-student-section {
        background: #f8f9fa;
        border-radius: 12px;
        padding: 20px;
        margin-bottom: 20px;
    }
    
    .selected-student-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 20px;
        padding-bottom: 15px;
        border-bottom: 1px solid #dee2e6;
    }
    
    .student-card {
        flex: 1;
    }
    
    .clear-button {
        background: #6c757d;
        color: white;
        border: none;
        padding: 8px 16px;
        border-radius: 6px;
        cursor: pointer;
        font-size: 14px;
    }
    
    .clear-button:hover {
        background: #5a6268;
    }
    
    .loading {
        text-align: center;
        padding: 40px;
        color: #666;
        font-style: italic;
    }
    
    .schedule-preview h3 {
        color: #333;
        margin-bottom: 15px;
    }
    
    .classes-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
        margin-bottom: 25px;
    }
    
    .class-item {
        background: white;
        padding: 15px;
        border-radius: 8px;
        border: 1px solid #dee2e6;
    }
    
    .class-day-time {
        font-weight: bold;
        color: #007bff;
        margin-bottom: 5px;
    }
    
    .class-subject {
        font-size: 16px;
        color: #333;
        margin-bottom: 5px;
    }
    
    .class-teacher {
        font-size: 14px;
        color: #666;
    }
    
    .message-preview-section {
        margin-bottom: 25px;
    }
    
    .message-preview-section h4 {
        color: #333;
        margin-bottom: 10px;
    }
    
    .message-preview {
        background: white;
        padding: 15px;
        border: 1px solid #dee2e6;
        border-radius: 8px;
        white-space: pre-line;
        font-family: monospace;
        font-size: 14px;
        color: #333;
        line-height: 1.4;
    }
    
    .send-section {
        text-align: center;
    }
    
    .send-button {
        background: #28a745;
        color: white;
        border: none;
        padding: 15px 30px;
        border-radius: 8px;
        font-size: 16px;
        font-weight: bold;
        cursor: pointer;
        transition: background-color 0.2s;
    }
    
    .send-button:hover:not(:disabled) {
        background: #218838;
    }
    
    .send-button:disabled {
        background: #6c757d;
        cursor: not-allowed;
    }
    
    .no-phone-warning {
        background: #f8d7da;
        color: #721c24;
        padding: 15px;
        border-radius: 8px;
        border: 1px solid #f5c6cb;
        text-align: center;
        font-weight: bold;
    }
    
    .no-classes {
        text-align: center;
        padding: 40px;
    }
    
    .no-classes h3 {
        color: #666;
        margin-bottom: 10px;
    }
    
    .no-classes p {
        color: #999;
        font-style: italic;
    }
    
    .success-message {
        background: #d4edda;
        color: #155724;
        padding: 15px;
        border-radius: 8px;
        border: 1px solid #c3e6cb;
        margin-top: 20px;
        text-align: center;
        font-weight: bold;
    }
    
    .error-message {
        background: #f8d7da;
        color: #721c24;
        padding: 15px;
        border-radius: 8px;
        border: 1px solid #f5c6cb;
        margin-top: 20px;
        text-align: center;
        font-weight: bold;
    }
</style>
